import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'
import Bugsnag from '@bugsnag/js'
import BugsnagPerformance from '@bugsnag/browser-performance'
import BugsnagPluginVue, {
  type BugsnagPluginVueResult
} from '@bugsnag/plugin-vue'
import {
  applyNativeAppVersion,
  attachOpenDialogMetadata,
  bugsnagErrorFilter,
  reclassifyBugsnagEvent,
  setNativeAppVersion
} from '@/utils/bugsnag-onerror'
import { registerPreloadErrorReload } from '@/utils/preload-error-handler'
import { startPeriodicUpdateChecks } from '@/services/pwa-update'
import App from './App.vue'
import router from './router'
import { createPersistedState } from 'pinia-plugin-persistedstate'
import { queryClient } from '@/composables/queries/query_client'
import { getClientShortSHA } from '@/utils/app-version'
import { getNativeAppInfo } from '@/utils/native-app-info'
import {
  applyNativePlatformFeatureFlags,
  nativePlatform
} from '@/utils/native-platform'
import { registerHardwareBackButton } from '@/utils/native-back-button'
import {
  loadPersistentStorage,
  persistentStorage
} from '@/services/persistent-storage'

import './assets/scss/main.scss'
import './assets/scss/utils.scss'

registerPreloadErrorReload()
startPeriodicUpdateChecks()
applyNativePlatformFeatureFlags()
registerHardwareBackButton(router)

const shortSHA = getClientShortSHA()
const bugsnagApiKey = import.meta.env.VITE_BUGSNAG_KEY
const releaseStage = import.meta.env.VITE_DEPLOY_ENV || 'DEVELOPMENT'
let bugsnagVue: BugsnagPluginVueResult | undefined

if (bugsnagApiKey) {
  Bugsnag.start({
    apiKey: bugsnagApiKey,
    plugins: [new BugsnagPluginVue()],
    releaseStage: releaseStage,
    appType: nativePlatform(),
    appVersion: shortSHA,
    onError: (event) => {
      applyNativeAppVersion(event)
      reclassifyBugsnagEvent(event)
      attachOpenDialogMetadata(event)
      return bugsnagErrorFilter(event)
    }
  })

  getNativeAppInfo().then((nativeAppInfo) => {
    if (nativeAppInfo) {
      setNativeAppVersion(nativeAppInfo)
      Bugsnag.addMetadata('native', {
        ...nativeAppInfo,
        webCommitSha: shortSHA
      })
    }
  })

  BugsnagPerformance.start({
    apiKey: bugsnagApiKey,
    bugsnag: Bugsnag,
    releaseStage: releaseStage
  })

  bugsnagVue = Bugsnag.getPlugin('vue')
}

loadPersistentStorage().finally(() => {
  const pinia = createPinia()
  pinia.use(createPersistedState({ storage: persistentStorage }))
  const app = createApp(App)
  app.use(pinia).use(router).use(VueQueryPlugin, {
    queryClient,
    enableDevtoolsV6Plugin: true
  })

  if (bugsnagVue) {
    app.use(bugsnagVue)
  }
  app.mount('#app')
})
